import type { ComponentPropsWithoutRef, ElementType } from 'react';
import { cn } from '@/lib/cn';
import type { CmsTypographyVariants } from '@/services/cms-sdk/schemas/models/CmsTypographyVariants';

export const variantSizeClasses: Record<CmsTypographyVariants, string> = {
    'prominent-xl': 'text-size-prominent-xl',
    'prominent-l': 'text-size-prominent-l',
    prominent: 'text-size-prominent',
    h1: 'text-size-h1',
    h2: 'text-size-h2',
    h3: 'text-size-h3',
    h4: 'text-size-h4',
    h5: 'text-size-h5',
    h6: 'text-size-h6',
    'paragraph-heavy': 'text-size-paragraph-heavy',
    paragraph: 'text-size-paragraph',
    'caption-heavy': 'text-size-caption-heavy',
    caption: 'text-size-caption',
};

export const responsiveVariantSizeClasses: Record<CmsTypographyVariants, string> = {
    'prominent-xl': 'text-size-prominent-l md:text-size-prominent-xl',
    'prominent-l': 'text-size-prominent md:text-size-prominent-l',
    prominent: 'text-size-h1 md:text-size-prominent',
    h1: 'text-size-h2 md:text-size-h1',
    h2: 'text-size-h3 md:text-size-h2',
    h3: 'text-size-h4 md:text-size-h3',
    h4: 'text-size-h5 md:text-size-h4',
    h5: 'text-size-h6 md:text-size-h5',
    h6: 'text-size-paragraph md:text-size-h6',
    'paragraph-heavy': 'text-size-caption md:text-size-paragraph-heavy',
    paragraph: 'text-size-caption md:text-size-paragraph',
    'caption-heavy': 'text-size-caption md:text-size-caption-heavy',
    caption: 'text-size-caption',
};

export const variantClasses: Record<CmsTypographyVariants, string> = {
    'prominent-xl': `font-bolden-van ${variantSizeClasses['prominent-xl']}`,
    'prominent-l': `font-bolden-van ${variantSizeClasses['prominent-l']}`,
    prominent: `font-bolden-van ${variantSizeClasses['prominent']}`,
    h1: `font-bold ${variantSizeClasses.h1}`,
    h2: `font-bold ${variantSizeClasses.h2}`,
    h3: `font-bold ${variantSizeClasses.h3}`,
    h4: `font-bold ${variantSizeClasses.h4}`,
    h5: `font-bold ${variantSizeClasses.h5}`,
    h6: `font-bold ${variantSizeClasses.h6}`,
    'paragraph-heavy': `font-semibold ${variantSizeClasses['paragraph-heavy']}`,
    paragraph: `font-normal ${variantSizeClasses.paragraph}`,
    'caption-heavy': `font-semibold ${variantSizeClasses['caption-heavy']}`,
    caption: `font-normal ${variantSizeClasses.caption}`,
};

export type TypographyProps<T extends ElementType = 'p'> = {
    as?: T;
    variant?: CmsTypographyVariants;
    className?: string;
    noProse?: boolean;
} & ComponentPropsWithoutRef<T>;

export function Typography<T extends ElementType = 'p'>({ as, className, variant = 'paragraph', noProse, ...props }: TypographyProps<T>) {
    const Component = as || 'p';
    return <Component className={cn('text-inherit', { prose: !noProse }, variantClasses[variant], className)} {...props} />;
}

export const levelToVariant: Record<2 | 3 | 4 | 5 | 6, CmsTypographyVariants> = {
    2: 'h2',
    3: 'h3',
    4: 'h4',
    5: 'h5',
    6: 'h6',
};
